import { useState } from 'react'
import _ from 'lodash'
import { useErrorHandler } from 'react-error-boundary'
import UiStateContainer from '@vzmi/ec-react-ui-state-container'
import { FormLoader, Toast } from '@vzmi/shp_shared_compo'
import { PROPOSAL_REVIEW_STATUS } from '@vzmi/shp_shared_compo/constants/proposal'
import { FRONT_END_DEFINED_ERROR_CODE } from '../../../constants/globalTypes'
import { withCommonPageErrorBoundary } from '../../Common/HOC/withErrorBoundary'
import { withI13nWrapper } from '../../Common/I13nWrapper'
import Anchor from '../../Common/Anchor/Anchor'
import withUpdateListingForm from '../Common/HOC/withUpdateListingForm'
import useListingsOnComposeInit from '../../BatchProposal/hook/useListingsOnComposeInit'
import useUpdateListingProposalOnOverviewInit from '../../BatchProposal/hook/useUpdateListingProposalOnOverviewInit'
import useBeforeunload from '../../BatchProposal/hook/useBeforeunload'
import { getBackgroundFormData } from '../../BatchProposal/hook/useBackgroundFormDataOnInit'
import useUIState from '../../BatchProposal/hook/useUIState'
import usePageFlow from '../../BatchProposal/hook/usePageFlow'
import { useQueryString } from '@vzmi/shp_shared_compo/hook/useQueryString'
import { FlowContext } from '../../BatchProposal/helper/flow'
import ContentLayout from '../../BatchProposal/Common/ContentLayout'
import Background from '../../BatchProposal/Common/Background'
import ActionHeader from '../../BatchProposal/Common/ActionHeader'
import { ActionFooterWithUIState } from '../../BatchProposal/Common/ActionFooter'
import {
  DEFAULT_MENU_DATA,
  getNonExistAttrMenuParams,
  getMenuResults,
  getCombinedMenuData,
  getCategoryOptionsMap
} from '../../BatchProposal/UpdateAttribute/helper'
import {
  getBatchResult,
  getCombinedBatchResult
} from '../../BatchProposal/helper/batch'
import {
  getFormStateFromStorage,
  clearStateFromStorage
} from '../../BatchProposal/helper/storage'
import { getFormValues } from '../Common/ProposalForm/Shared/util'
import ProposalConsent from '../Common/ProposalConsent/ProposalConsent'
import SuccessPage from './SuccessPage'
import PageTitle from './PageTitle'
import SubProposalsStatusBar from './SubProposalsStatusBar'
import UpdateImage from './UpdateImage'
import TitleDescription from './UpdateImage/TitleDescription'
import UpdateAttribute from './UpdateAttribute'
import UpdateVideo, { getCustomVideoSectionTitle } from './UpdateVideo'
import UpdateCopy from './UpdateCopy'
import UpdateBrandID from './UpdateBrandID'
import AnchorProposalSection from './AnchorProposalSection'
import {
  ANCHOR_MAP,
  ANCHORS,
  getSubStationLabel,
  getConvertedListing,
  getFormListingWithFilledAttributes,
  getParams,
  submitProposal,
  getFieldErrors,
  getListingWithProposal,
  getUpdatedSubProposals
} from './helper'
import useI13N from './hook/useI13N'

const UpdateListingInfo = props => {
  const {
    match: { params: { phase } },
    dispatch,
    change,
    initialize,
    proposal: proposalSetting,
    flags,
    actions,
    reduxFormState,
    user,
    supplier,
    proposalType,
    i13nMethods
  } = props
  const formValues = getFormValues(reduxFormState)
  const { state: pageState, actions: pageActions } = useUIState(props)
  const pageFlow = usePageFlow(phase, pageState.pageFlow)
  const {
    isEditPage,
    isConfirmPage,
    isSuccessPage,
    isOverviewPage,
    readOnly
  } = pageFlow

  // 取得當前賣場詳情資料
  const copy = _.get(formValues, 'copy', '')

  const initFormWithBackground = (form, subStationId) => {
    const defaultBackgroundData = getBackgroundFormData(props)
    const subStationLabel = getSubStationLabel(proposalSetting, subStationId)
    const formWithBackground = {
      ...form,
      // 取得賣場才寫入賣場背景資料，因為會用到子站編號顯示提案站別
      ...defaultBackgroundData,
      subStationLabel
    }
    initialize(formWithBackground)
  }
  // 在修改、確認、完成頁加上 GA 紀錄
  const i13nData = { proposalType }
  useI13N(i13nMethods, i13nData, pageFlow, formValues)

  // 修改頁綁上離開頁面確認事件
  useBeforeunload(isOverviewPage)

  const handleError = useErrorHandler()

  const currentQueryStringListingId = useQueryString(props, 'listingId', true)
  const listingsParams = { id: currentQueryStringListingId }
  const [listing, setListing] = useState({})
  // 修改頁初始化取得批次賣場資料
  useListingsOnComposeInit(props, listingsParams, isEditPage,
    () => pageActions.setIsLoading(true),
    async (error, fetchedListings) => {
      if (error) {
        pageActions.setIsLoading(false)
        Toast.error(error.message)
        return
      }

      const fetchedListing = _.first(fetchedListings)
      // 查無賣場
      if (_.isEmpty(fetchedListing)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_LISTING_NOT_FOUND })
        return
      }

      const savedFormListing = getFormStateFromStorage()
      const isSavedFormListingExist = !_.isEmpty(savedFormListing)
      const convertedListings = isSavedFormListingExist
        ? [savedFormListing]
        // 從 get listing 轉換資料成包含 redux form field
        : [getConvertedListing(fetchedListing)]
      // 取得修改規格表選單資料
      const updatedMenuData = await updateMenuData(convertedListings)
      const updatedListing = isSavedFormListingExist
        ? savedFormListing
        // 賣場填入修改規格表、商品規格資料
        : getFormListingWithFilledAttributes(convertedListings, updatedMenuData)
      updateCategoryOptions(convertedListings, updatedMenuData)
      setListing(updatedListing)
      pageActions.setIsLoading(false)
      // 設定 redux form 預設值
      const { subStationId } = updatedListing
      initFormWithBackground(updatedListing, subStationId)
      clearStateFromStorage()
    }
  )

  const currentQueryStringProposalId = useQueryString(props, 'id', true)
  const proposalParams = {
    proposal: {
      id: currentQueryStringProposalId
    },
    listing: {}
  }
  const [subProposalMap, setSubProposalMap] = useState({})
  const [batchResult, setBatchResult] = useState(getBatchResult([]))
  // 從提案內取得建立提案人名稱
  const proposalApplicant = _.get(batchResult.successProposalMap, `${listing.id}.applicant`)
  const proposalUser = isOverviewPage
    ? _.merge(user, { identity: { name: proposalApplicant } })
    : user
  useUpdateListingProposalOnOverviewInit(props, proposalParams, isOverviewPage,
    () => pageActions.setIsLoading(true),
    async (error, currentProposal, currentListing, subProposalMapByType) => {
      if (error) {
        pageActions.setIsLoading(false)
        Toast.error(error.message)
        return
      }

      // 查無提案
      if (_.isEmpty(currentProposal)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_PROPOSAL_NOT_FOUND })
        return
      }

      // 查無賣場
      if (_.isEmpty(currentListing)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_LISTING_NOT_FOUND })
        return
      }

      const currentBatchResult = getBatchResult([currentProposal].map(proposal => ([undefined, proposal])))
      const convertedListing = _.flow(
        () => getListingWithProposal(currentListing, currentBatchResult, subProposalMapByType),
        listingWithProposal => getConvertedListing(listingWithProposal, subProposalMapByType)
      )()
      const convertedListings = [convertedListing]
      const { subStationId } = currentListing
      const updatedMenuData = await updateMenuData(convertedListings)
      updateCategoryOptions(convertedListings, updatedMenuData)
      const updatedListing = getFormListingWithFilledAttributes(convertedListings, updatedMenuData)
      pageActions.setIsLoading(false)
      setListing(updatedListing)
      setBatchResult(currentBatchResult)
      setSubProposalMap(subProposalMapByType)
      initFormWithBackground(updatedListing, subStationId)
    }
  )

  const [menuData, setMenuData] = useState(DEFAULT_MENU_DATA)
  const updateMenuData = async (currentListings) => {
    const nonExistAttrMenuParams = getNonExistAttrMenuParams(currentListings, menuData)
    const results = await getMenuResults(nonExistAttrMenuParams, actions)
    const newMenuData = getCombinedMenuData(menuData, results)
    setMenuData(newMenuData)
    return newMenuData
  }

  const [selectedCategoryOptionByListingId, setSelectedCategoryOptionByListingId] = useState({})
  const [categoryOptionsBySubStationId, setCategoryOptionsBySubStationId] = useState({})
  const updateCategoryOptions = (currentListings, currentMenuData, isUpdateSelectedOptions = true) => {
    // 既有的對應表
    const originMap = {
      // 按照賣場分類已選擇的規格表選單項目[父類項目、子類項目、規格項目]
      listing: selectedCategoryOptionByListingId,
      // 按照子站分類有選擇過的完整規格表選單
      subStation: categoryOptionsBySubStationId
    }
    const {
      listing: currentSelectedCategoryOptionByListingId,
      subStation: currentCategoryOptionsBySubStationId
    } = getCategoryOptionsMap(currentListings, currentMenuData, originMap)
    // 更新選單父類、子類選項時不需要把賣場真正選擇的選項更新掉
    // 因為選單有三層，可能選完前兩層就離開選單，需要幫他還原
    if (isUpdateSelectedOptions) {
      setSelectedCategoryOptionByListingId(currentSelectedCategoryOptionByListingId)
    }
    setCategoryOptionsBySubStationId(currentCategoryOptionsBySubStationId)
    return [
      currentSelectedCategoryOptionByListingId,
      currentCategoryOptionsBySubStationId
    ]
  }

  const updateFormListingWithAttributes = (listings, currentMenuData) => {
    const formListingWithFilledAttributes = getFormListingWithFilledAttributes(listings, currentMenuData)
    initialize(formListingWithFilledAttributes)
  }

  // 規格表選單選擇未選擇過的項目會需要發出請求，請求過程中該選項會維持 loading 狀態從這裡控制
  const triggerCategoryOptionLoadingState = (listing, catOption, catItemOption) => {
    const { subStationId } = listing
    const currentCategoryOptions = _.cloneDeep(
      _.get(categoryOptionsBySubStationId, subStationId, [])
    )
    const selectCatIndex = _.get(catOption, 'index')
    if (_.isUndefined(catItemOption)) {
      _.set(currentCategoryOptions, `${selectCatIndex}.loading`, true)
      setCategoryOptionsBySubStationId({
        ...categoryOptionsBySubStationId,
        [subStationId]: currentCategoryOptions
      })
      return
    }

    const catItemIndex = _.get(catItemOption, 'index')
    _.set(currentCategoryOptions, `${selectCatIndex}.children[${catItemIndex}].loading`, true)
    setCategoryOptionsBySubStationId({
      ...categoryOptionsBySubStationId,
      [subStationId]: currentCategoryOptions
    })
  }

  const [totalSubProposals, setTotalSetSubProposals] = useState([])
  const onComplete = async () => {
    const { id: listingId } = formValues
    const params = getParams(formValues, PROPOSAL_REVIEW_STATUS.DRAFT, totalSubProposals)
    // [[error, undefined, {}], [undefined, proposal, subProposalMapByType]]
    const proposals = await submitProposal(actions, params)
    const combinedBatchResult = getCombinedBatchResult(
      batchResult,
      getBatchResult(proposals)
    )
    const {
      failedProposalMap,
      successProposalMap
    } = combinedBatchResult
    // 以最新送出的提案取得實際上有送出的區塊，用於判斷哪些區塊沒有同時修改
    const mainSubProposalMapByType = _.flow(
      () => _.get(successProposalMap, listingId, {}),
      updateListingProposal => _.get(updateListingProposal, 'subProposals', []),
      subProposals => _.keyBy(subProposals, 'type')
    )()
    // post updateListing 後會再打一個 get updateImage proposal 才能取得全部 size 的 models images
    const subProposalMapByType = _.get(proposals, '[0][2]', {})
    const convertedListing = _.flow(
      () => getListingWithProposal(formValues, combinedBatchResult, subProposalMapByType),
      listingWithProposal => getConvertedListing(listingWithProposal, mainSubProposalMapByType)
    )()
    const fieldErrors = getFieldErrors(listing, failedProposalMap)
    // 提案請求有錯誤訊息時 success map 會找不到這個賣場的資料
    const isProposalSuccess = (listingId in successProposalMap)
    const convertedListings = isProposalSuccess
      ? [convertedListing]
      // 返回 null 可以讓 ActionFooter 知道 submit 有失敗，執行對應失敗的行為
      : null
    setBatchResult(combinedBatchResult)
    if (isProposalSuccess) {
      const updatedMenuData = await updateMenuData(convertedListings)
      const updatedListing = getFormListingWithFilledAttributes(convertedListings, updatedMenuData)
      const updatedSubProposals = getUpdatedSubProposals(totalSubProposals, updatedListing)
      // 提案成功在 redux form 增加 proposal 資料
      initialize(updatedListing)
      // 更新送出過的子提案，用於返回修改後可以更新送出過的子提案
      setTotalSetSubProposals(updatedSubProposals)
    }
    // 提案失敗且非指定欄位失敗跳 toast 顯示錯誤訊息
    const errorMessage = _.get(failedProposalMap, `${listingId}.message`)
    return [errorMessage, convertedListings, fieldErrors]
  }

  const onPendingReview = async () => {
    const { id: listingId } = formValues
    const params = getParams(formValues, PROPOSAL_REVIEW_STATUS.PENDING_REVIEW, totalSubProposals)
    const proposals = await submitProposal(actions, params)
    const combinedBatchResult = getCombinedBatchResult(
      batchResult,
      getBatchResult(proposals)
    )
    const {
      failedProposalMap,
      successProposalMap
    } = combinedBatchResult
    // 完成頁不需要 subProposalMapByType 因為只需要顯示簡單的畫面
    const listingWithProposal = getListingWithProposal(formValues, combinedBatchResult, {})
    const isProposalSuccess = (listingId in successProposalMap)
    const updatedListings = isProposalSuccess
      ? [listingWithProposal]
      : null
    // 提案失敗且非指定欄位失敗跳 toast 顯示錯誤訊息
    const errorMessage = _.get(failedProposalMap, `${listingId}.message`)
    return [errorMessage, updatedListings]
  }

  if (_.isEmpty(listing)) {
    return (
      <FormLoader
        isLoading
        fixed
      />
    )
  }

  // header footer context
  // 避免在主要表單中使用此 context 避免重複 render component
  const flowContextValue = {
    phase,
    pageFlow: pageState.pageFlow,
    isLoading: pageState.isLoading,
    isPending: pageState.isPending,
    setPageFlow: pageActions.setPageFlow,
    setIsLoading: pageActions.setIsLoading,
    setIsPending: pageActions.setIsPending
  }

  return (
    <ContentLayout isSuccessPage={isSuccessPage}>
      <FormLoader
        isLoading={pageState.isLoading}
        fixed
      >
        {isSuccessPage && (
          <SuccessPage />
        )}
        {
          (!isSuccessPage) && (
            <UiStateContainer>
              <FlowContext.Provider value={flowContextValue}>
                <ActionHeader
                  title={<PageTitle />}
                  listingIds={[listing.id]}
                  isBatchProposal={false}
                  proposalType={proposalType}
                />
              </FlowContext.Provider>
              <Anchor
                key='anchor'
                items={ANCHORS}
                withDashboardMode={false}
                withCenterItemsModel
                withHashLink={false}
              />
              {
                isOverviewPage && (
                  // 提案狀態
                  <SubProposalsStatusBar
                    listing={formValues}
                    subProposalMap={subProposalMap}
                  />
                )
              }
              {/* 提案背景 */}
              <Background
                flags={flags}
                user={proposalUser}
                isEditPage={isEditPage}
                readOnly={readOnly}
                withSubStation
              />
              {isConfirmPage && <ProposalConsent />}
              {/* 賣場與圖片 */}
              <AnchorProposalSection
                flags={flags}
                anchor={ANCHOR_MAP.UPDATE_IMAGE}
                description={<TitleDescription />}
                component={UpdateImage}
                // 避免拿到舊的狀態用 redux form 的最即時，因為有用到 change 修改可能變動的表單資料
                listing={formValues}
                readOnly={readOnly}
                change={change}
                supplier={supplier}
                actions={actions}
                withoutCheckbox
                proposalType={proposalType}
              />
              {/* 品牌ID */}
              <UpdateBrandID
                flags={flags}
                listing={formValues}
                component={UpdateBrandID}
                readOnly={readOnly}
                change={change}
                withoutCheckbox
                proposalType={proposalType}
                actions={actions}
              />
              {/* 商品規格 */}
              <AnchorProposalSection
                anchor={ANCHOR_MAP.UPDATE_ATTRIBUTE}
                component={UpdateAttribute}
                listing={formValues}
                initialize={initialize}
                menuData={menuData}
                selectedOptionMap={selectedCategoryOptionByListingId}
                optionsMap={categoryOptionsBySubStationId}
                updateMenuData={updateMenuData}
                updateCategoryOptions={updateCategoryOptions}
                updateFormListingWithAttributes={updateFormListingWithAttributes}
                triggerOptionLoadingState={triggerCategoryOptionLoadingState}
                readOnly={readOnly}
              />
              {/* 商品詳情與保證 */}
              <AnchorProposalSection
                anchor={ANCHOR_MAP.UPDATE_COPY}
                component={UpdateCopy}
                readOnly={readOnly}
                change={change}
                flags={flags}
                user={user}
                copy={copy}
                isEditPage={isEditPage}
              />
              {/* 賣場影片 */}
              <AnchorProposalSection
                anchor={ANCHOR_MAP.UPDATE_VIDEO}
                component={UpdateVideo}
                customTitle={getCustomVideoSectionTitle}
                readOnly={readOnly}
              />
              <FlowContext.Provider value={flowContextValue}>
                <ActionFooterWithUIState
                  dispatch={dispatch}
                  batchResult={batchResult}
                  onComplete={onComplete}
                  onPendingReview={onPendingReview}
                  isBatchProposal={false}
                />
              </FlowContext.Provider>
            </UiStateContainer>
          )
        }
      </FormLoader>
    </ContentLayout>
  )
}

const hoc = _.flow([
  withI13nWrapper,
  // common page: yahoo title & supplier part
  // error boundary
  withCommonPageErrorBoundary(),
  // proposal redux form init
  withUpdateListingForm
])

export default hoc(UpdateListingInfo)
